import { supabase, typedSupabase, type Database } from '../lib/supabase'
import type { Account } from '../types/account.types'
import { AccountType } from '../types/account.types'
import { calculateCurrentBalance } from '../utils/balance-calculator'
import type { Transaction } from '../types/transaction.types'

// Database service for accounts
export class SupabaseAccountService {
  private tableName = 'accounts'

  // Get all accounts for a user
  async getAccounts(userId: string, filters?: {
    type?: string
    status?: 'active' | 'closed'
    includeInNetWorth?: boolean
    category?: string
    page?: number
    limit?: number
  }) {
    try {
      let query: any = (typedSupabase
        .from(this.tableName as 'accounts')
        .select('*')
        .eq('user_id', userId) as any)

      // Apply filters
      if (filters?.type) {
        query = query.eq('type', filters.type)
      }
      
      if (filters?.status) {
        const isActive = filters.status === 'active'
        query = query.eq('is_active', isActive)
      }
      
      if (filters?.includeInNetWorth !== undefined) {
        query = query.eq('include_in_net_worth', filters.includeInNetWorth)
      }
      
      if (filters?.category) {
        query = query.eq('category', filters.category)
      }

      // Apply pagination
      if (filters?.page && filters?.limit) {
        const from = (filters.page - 1) * filters.limit
        const to = from + filters.limit - 1
        query = query.range(from, to)
      }

      const { data, error, count } = await query

      if (error) {
        throw new Error(`Failed to fetch accounts: ${error.message}`)
      }

      // Type assertion needed because Database type may be incomplete
      const accounts = (data || []) as Database['public']['Tables']['accounts']['Row'][]

      return {
        accounts: accounts,
        total: count || 0
      }
    } catch (error) {
      console.error('Error fetching accounts:', error)
      throw error
    }
  }

  // Get a single account by ID
  async getAccountById(accountId: string, userId: string) {
    try {
      const { data, error } = await (typedSupabase
        .from(this.tableName as 'accounts')
        .select('*')
        .eq('id', accountId)
        .eq('user_id', userId)
        .single() as any)

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Account not found')
        }
        throw new Error(`Failed to fetch account: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error fetching account:', error)
      throw error
    }
  }

  // Create a new account
  async createAccount(accountData: Omit<Account, 'id' | 'createdAt' | 'updatedAt'>) {
    try {
      const insertData: any = {
        user_id: accountData.userId,
        name: accountData.name,
        type: accountData.type,
        provider: accountData.provider,
        account_number: accountData.accountNumber,
        opening_balance: accountData.openingBalance,
        current_balance: accountData.currentBalance,
        opening_date: accountData.openingDate,
        is_active: accountData.isActive,
        include_in_net_worth: accountData.includeInNetWorth,
        notes: accountData.notes,
        credit_limit: accountData.creditLimit,
        interest_rate: accountData.interestRate,
        payment_due_date: accountData.paymentDueDate,
        renewal_date: accountData.renewalDate,
        loan_amount: accountData.loanAmount,
        last_payment_due: accountData.lastPaymentDue,
        interest_calculated_day: accountData.interestCalculatedDay,
        loan_purpose: accountData.loanPurpose,
        linked_account_id: accountData.linkedAccountId,
        lease_end_date: accountData.leaseEndDate,
        residual_value: accountData.residualValue,
        employer_name: accountData.employerName
      }

      // Add asset-specific fields if type is ASSETS
      // Use AccountType.ASSETS enum value (which is 'assets') for comparison
      if (accountData.type === AccountType.ASSETS) {
        // Ensure enum values are converted to strings (they should already be strings, but be explicit)
        // Handle both enum values and string values
        const categoryValue = accountData.category || (accountData as any).category;
        const subTypeValue = accountData.subType || (accountData as any).subType;
        const currentValue = accountData.currentValue ?? (accountData as any).currentValue;
        
        // Validate required fields BEFORE setting insertData
        if (!categoryValue || !subTypeValue || currentValue === null || currentValue === undefined) {
          const missingFields = [];
          if (!categoryValue) missingFields.push('category');
          if (!subTypeValue) missingFields.push('subType');
          if (currentValue === null || currentValue === undefined) missingFields.push('currentValue');
          throw new Error(`Assets require the following fields to be set: ${missingFields.join(', ')}. Received: category=${categoryValue}, subType=${subTypeValue}, currentValue=${currentValue}`)
        }
        
        insertData.category = String(categoryValue)
        insertData.sub_type = String(subTypeValue)
        insertData.current_value = Number(currentValue)
        insertData.purchase_price = accountData.purchasePrice ? Number(accountData.purchasePrice) : null
        insertData.purchase_date = accountData.purchaseDate ?? null
        insertData.valuation_source = accountData.valuationSource ? String(accountData.valuationSource) : 'manual'
        insertData.valuation_provider = accountData.valuationProvider ?? null
        insertData.last_valuation_date = accountData.lastValuationDate ?? null
        insertData.value_behaviour = accountData.valueBehaviour ? String(accountData.valueBehaviour) : null
        insertData.depreciation_method = accountData.depreciationMethod ? String(accountData.depreciationMethod) : null
        insertData.depreciation_rate_percent = accountData.depreciationRatePercent ?? null
        insertData.auto_update_value = accountData.autoUpdateValue ?? false
      }

      const { data, error } = await (typedSupabase
        .from(this.tableName as 'accounts')
        .insert(insertData)
        .select()
        .single() as any)

      if (error) {
        throw new Error(`Failed to create account: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error creating account:', error)
      throw error
    }
  }

  // Update an account
  async updateAccount(accountId: string, userId: string, updateData: Partial<Account>) {
    try {
      const updatePayload: any = {
        name: updateData.name,
        type: updateData.type,
        provider: updateData.provider,
        account_number: updateData.accountNumber,
        opening_balance: updateData.openingBalance,
        current_balance: updateData.currentBalance,
        opening_date: updateData.openingDate,
        is_active: updateData.isActive,
        include_in_net_worth: updateData.includeInNetWorth,
        notes: updateData.notes,
        closure_date: updateData.closureDate,
        closure_reason: updateData.closureReason,
        credit_limit: updateData.creditLimit,
        interest_rate: updateData.interestRate,
        payment_due_date: updateData.paymentDueDate,
        renewal_date: updateData.renewalDate,
        loan_amount: updateData.loanAmount,
        last_payment_due: updateData.lastPaymentDue,
        interest_calculated_day: updateData.interestCalculatedDay,
        updated_at: new Date().toISOString()
      }

      // Loan purpose / linking
      if (updateData.loanPurpose !== undefined) updatePayload.loan_purpose = updateData.loanPurpose
      if (updateData.linkedAccountId !== undefined) updatePayload.linked_account_id = updateData.linkedAccountId
      if (updateData.leaseEndDate !== undefined) updatePayload.lease_end_date = updateData.leaseEndDate
      if (updateData.residualValue !== undefined) updatePayload.residual_value = updateData.residualValue
      if (updateData.employerName !== undefined) updatePayload.employer_name = updateData.employerName

      // Add asset-specific fields if they are being updated
      if (updateData.category !== undefined) updatePayload.category = updateData.category
      if (updateData.subType !== undefined) updatePayload.sub_type = updateData.subType
      if (updateData.currentValue !== undefined) updatePayload.current_value = updateData.currentValue
      if (updateData.purchasePrice !== undefined) updatePayload.purchase_price = updateData.purchasePrice
      if (updateData.purchaseDate !== undefined) {
        updatePayload.purchase_date = updateData.purchaseDate
        // Keep opening_date aligned with purchase_date for assets when not provided separately
        if (updateData.openingDate === undefined) {
          updatePayload.opening_date = updateData.purchaseDate
        }
      }
      if (updateData.valuationSource !== undefined) updatePayload.valuation_source = updateData.valuationSource
      if (updateData.valuationProvider !== undefined) updatePayload.valuation_provider = updateData.valuationProvider
      if (updateData.lastValuationDate !== undefined) updatePayload.last_valuation_date = updateData.lastValuationDate
      if (updateData.valueBehaviour !== undefined) updatePayload.value_behaviour = updateData.valueBehaviour
      if (updateData.depreciationMethod !== undefined) updatePayload.depreciation_method = updateData.depreciationMethod
      if (updateData.depreciationRatePercent !== undefined) updatePayload.depreciation_rate_percent = updateData.depreciationRatePercent
      if (updateData.autoUpdateValue !== undefined) updatePayload.auto_update_value = updateData.autoUpdateValue

      const { data, error } = await (typedSupabase
        .from(this.tableName as 'accounts')
        .update(updatePayload)
        .eq('id', accountId)
        .eq('user_id', userId)
        .select()
        .single() as any)

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Account not found')
        }
        throw new Error(`Failed to update account: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error updating account:', error)
      throw error
    }
  }

  // Delete an account
  async deleteAccount(accountId: string, userId: string) {
    try {
      const { error } = await (typedSupabase
        .from(this.tableName as 'accounts')
        .delete()
        .eq('id', accountId) as any)
        .eq('user_id', userId)

      if (error) {
        throw new Error(`Failed to delete account: ${error.message}`)
      }

      return true
    } catch (error) {
      console.error('Error deleting account:', error)
      throw error
    }
  }

  // Close an account
  async closeAccount(accountId: string, userId: string, closureData: {
    closureDate: string
    closureReason?: string
    includeInNetWorth?: boolean
    currentValue?: number
  }) {
    try {
      const updatePayload: Record<string, unknown> = {
        is_active: false,
        closure_date: closureData.closureDate,
        closure_reason: closureData.closureReason,
        updated_at: new Date().toISOString()
      }

      if (closureData.includeInNetWorth !== undefined) {
        updatePayload.include_in_net_worth = closureData.includeInNetWorth
      }
      if (closureData.currentValue !== undefined) {
        updatePayload.current_value = closureData.currentValue
      }

      const { data, error } = await (typedSupabase
        .from(this.tableName as 'accounts')
        .update(updatePayload)
        .eq('id', accountId)
        .eq('user_id', userId)
        .eq('is_active', true) // Only close active accounts
        .select()
        .single() as any)

      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error('Account not found or already closed')
        }
        throw new Error(`Failed to close account: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error closing account:', error)
      throw error
    }
  }

  // Helper function to convert database row to Transaction
  private convertDbRowToTransaction(row: any): Transaction {
    return {
      id: row.id,
      userId: row.user_id,
      accountId: row.account_id,
      categoryId: row.category_id || undefined,
      amount: parseFloat(row.amount) || 0,
      description: row.description || '',
      merchantName: row.merchant_name || undefined,
      date: row.date,
      type: row.type,
      status: row.status || 'pending',
      notes: row.notes || undefined,
      referenceNumber: row.reference_number || undefined,
      payee: row.payee || undefined,
      balanceAfter: row.balance_after ? parseFloat(row.balance_after) : undefined,
      sourceAccountId: row.source_account_id || undefined,
      destinationAccountId: row.destination_account_id || undefined,
      externalId: row.external_id || undefined,
      originalDescription: row.original_description || undefined,
      chequeNumber: row.cheque_number || undefined,
      autoCategorized: row.auto_categorized || false,
      confidenceScore: row.confidence_score ? parseFloat(row.confidence_score) : undefined,
      flaggedForReview: row.flagged_for_review || false,
      statusReason: row.status_reason || undefined,
      reviewedBy: row.reviewed_by || undefined,
      reviewedAt: row.reviewed_at || undefined,
      archivedAt: row.archived_at || undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  // Export accounts to CSV format
  async exportAccountsToCSV(userId: string, filters?: {
    type?: string
    status?: 'active' | 'closed'
  }) {
    try {
      let query: any = (typedSupabase
        .from(this.tableName as 'accounts')
        .select('*')
        .eq('user_id', userId) as any)

      if (filters?.type) {
        query = query.eq('type', filters.type)
      }
      
      if (filters?.status) {
        const isActive = filters.status === 'active'
        query = query.eq('is_active', isActive)
      }

      const { data, error } = await query

      if (error) {
        throw new Error(`Failed to export accounts: ${error.message}`)
      }

      // Type assertion needed because Database type may be incomplete
      const accounts = (data || []) as Database['public']['Tables']['accounts']['Row'][]
      
      // Calculate balances from transactions for each account
      const accountsWithBalances = await Promise.all(
        accounts.map(async (account) => {
          try {
            // Fetch regular transactions for this account
            const { data: regularTransactions } = await supabase
              .from('transactions')
              .select('*')
              .eq('user_id', userId)
              .eq('account_id', account.id)
              .is('archived_at', null)
              .order('date', { ascending: true })
              .limit(10000);

            // Fetch transfer transactions where this account is source or destination
            const { data: transferTransactions } = await supabase
              .from('transactions')
              .select('*')
              .eq('user_id', userId)
              .eq('type', 'transfer')
              .is('archived_at', null)
              .or(`source_account_id.eq.${account.id},destination_account_id.eq.${account.id}`)
              .order('date', { ascending: true })
              .limit(10000);

            // Combine and deduplicate transactions
            const transactionIds = new Set<string>();
            const allTransactions: Transaction[] = [];
            
            (regularTransactions || []).forEach(txn => {
              if (!transactionIds.has(txn.id)) {
                transactionIds.add(txn.id);
                allTransactions.push(this.convertDbRowToTransaction(txn));
              }
            });
            
            (transferTransactions || []).forEach(txn => {
              if (!transactionIds.has(txn.id)) {
                transactionIds.add(txn.id);
                allTransactions.push(this.convertDbRowToTransaction(txn));
              }
            });

            // Sort by date
            allTransactions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

            // Calculate current balance from transactions
            const calculatedBalance = calculateCurrentBalance(
              account.opening_balance || 0,
              account.opening_date || account.created_at || new Date().toISOString(),
              allTransactions,
              account.type as AccountType,
              account.id
            );

            return {
              ...account,
              calculatedBalance
            };
          } catch (err) {
            console.error(`Error calculating balance for account ${account.name}:`, err);
            // Return account with stored balance on error
            return {
              ...account,
              calculatedBalance: account.current_balance || account.opening_balance || 0
            };
          }
        })
      );

      // Convert to CSV format
      const csvHeader = 'Name,Type,Provider,Account Number,Opening Balance,Current Balance,Opening Date,Status,Include in Net Worth,Notes,Created At,Updated At,Credit Limit,Interest Rate,Payment Due Date,Renewal Date,Loan Amount,Last Payment Due,Interest Calculated Day,Category,Sub Type,Current Value,Purchase Price,Purchase Date,Valuation Source,Last Valuation Date'
      
      const csvRows = accountsWithBalances.map(account => 
        `"${account.name}","${account.type}","${account.provider}","${account.account_number}",${account.opening_balance},${account.calculatedBalance},"${account.opening_date}","${account.is_active ? 'Active' : 'Closed'}","${account.include_in_net_worth ? 'Yes' : 'No'}","${account.notes || ''}","${account.created_at}","${account.updated_at}","${account.credit_limit || ''}","${account.interest_rate || ''}","${account.payment_due_date || ''}","${account.renewal_date || ''}","${account.loan_amount || ''}","${account.last_payment_due || ''}","${account.interest_calculated_day || ''}","${account.category || ''}","${account.sub_type || ''}","${account.current_value || ''}","${account.purchase_price || ''}","${account.purchase_date || ''}","${account.valuation_source || ''}","${account.last_valuation_date || ''}"`
      )
      
      return csvRows.length > 0 ? [csvHeader, ...csvRows].join('\n') : csvHeader + '\n'
    } catch (error) {
      console.error('Error exporting accounts:', error)
      throw error
    }
  }

  // Subscribe to real-time changes
  subscribeToAccounts(userId: string, callback: (payload: any) => void) {
    return typedSupabase
      .channel('accounts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: this.tableName,
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      .subscribe()
  }
}

// Create a singleton instance
export const supabaseAccountService = new SupabaseAccountService()

// Authentication helpers
export class SupabaseAuthService {
  // Sign up a new user
  async signUp(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })

      if (error) {
        throw new Error(`Sign up failed: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error signing up:', error)
      throw error
    }
  }

  // Sign in an existing user
  async signIn(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        throw new Error(`Sign in failed: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error signing in:', error)
      throw error
    }
  }

  // Sign out the current user
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        throw new Error(`Sign out failed: ${error.message}`)
      }

      return true
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }

  // Get the current user
  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()

      if (error) {
        throw new Error(`Failed to get current user: ${error.message}`)
      }

      return user
    } catch (error) {
      console.error('Error getting current user:', error)
      throw error
    }
  }

  // Subscribe to auth state changes
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }

  // Resend confirmation email
  async resendConfirmationEmail(email: string) {
    try {
      // Validate email format first
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address');
      }

      // Try the resend API
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
      })

      if (error) {
        // Supabase's resend API can fail if:
        // 1. User doesn't exist in auth.users
        // 2. User is already confirmed
        // 3. Email service is not configured
        // 4. Rate limiting
        
        // Provide helpful error message
        if (error.message.includes('invalid') || error.message.includes('Invalid')) {
          throw new Error('Unable to resend confirmation email. This may happen if:\n• The email is already confirmed\n• The email is not registered\n• Email service is not configured\n\nPlease check your email inbox or confirm the user manually in Supabase Dashboard.')
        }
        
        throw new Error(`Failed to resend confirmation email: ${error.message}`)
      }

      return { success: true, data }
    } catch (error: any) {
      console.error('Error resending confirmation email:', error)
      throw error
    }
  }

  // Change email address
  async changeEmail(input: { newEmail: string }) {
    try {
      const { getCurrentUserId } = await import('../lib/supabase');
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(input.newEmail)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid email address'
          }
        };
      }

      // Update email (Supabase will send verification email)
      const { error } = await supabase.auth.updateUser({
        email: input.newEmail
      });

      if (error) {
        if (error.message.includes('already registered') || error.message.includes('already in use')) {
          return {
            error: {
              code: 'CONFLICT_ERROR',
              message: 'Email address is already in use'
            }
          };
        }
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: error.message || 'Failed to change email'
          }
        };
      }

      return {
        data: {
          message: 'Verification email sent to new address. Please check your email to confirm the change.',
          verificationEmailSent: true
        }
      };
    } catch (error: any) {
      console.error('Error changing email:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to change email'
        }
      };
    }
  }

  // Reset password for email (forgot password)
  async resetPasswordForEmail(email: string) {
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Please enter a valid email address'
          }
        };
      }

      // Get the current site URL for redirect
      const redirectUrl = `${window.location.origin}/reset-password`;

      // Send password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('Password reset error:', error);
        
        // Provide more helpful error messages for common issues
        if (error.message.includes('redirect_to') || error.message.includes('redirect')) {
          return {
            error: {
              code: 'CONFIGURATION_ERROR',
              message: 'Password reset is not properly configured. Please contact support.'
            }
          };
        }
        
        // Don't reveal if email exists or not for security
        // Supabase will return success even if email doesn't exist (to prevent email enumeration)
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: 'Failed to send password reset email. Please try again or contact support if the issue persists.'
          }
        };
      }

      return {
        data: {
          success: true,
          message: 'If an account exists with this email, you will receive a password reset link shortly.'
        }
      };
    } catch (error: any) {
      console.error('Error resetting password:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to send password reset email'
        }
      };
    }
  }

  // Change password
  async changePassword(input: { currentPassword: string; newPassword: string; confirmPassword: string }) {
    try {
      const { getCurrentUserId } = await import('../lib/supabase');
      const userId = await getCurrentUserId();
      
      if (!userId) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'User is not authenticated'
          }
        };
      }

      // Validate passwords match
      if (input.newPassword !== input.confirmPassword) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Passwords do not match'
          }
        };
      }

      // Validate password length
      if (input.newPassword.length < 8) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Password must be at least 8 characters'
          }
        };
      }

      // Validate current password is provided
      if (!input.currentPassword || input.currentPassword.length === 0) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Current password is required'
          }
        };
      }

      // Get current user email for verification
      const { data: { user }, error: getUserError } = await supabase.auth.getUser();
      
      if (getUserError || !user || !user.email) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'Failed to get current user'
          }
        };
      }

      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: input.currentPassword
      });

      if (signInError) {
        return {
          error: {
            code: 'AUTH_ERROR',
            message: 'Current password is incorrect'
          }
        };
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: input.newPassword
      });

      if (updateError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: updateError.message || 'Failed to change password'
          }
        };
      }

      return {
        data: {
          success: true
        }
      };
    } catch (error: any) {
      console.error('Error changing password:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to change password'
        }
      };
    }
  }
}

// Create a singleton instance
export const supabaseAuthService = new SupabaseAuthService()
